/**
 * Dati del grafico di V68: assi, dose di squadra e serie per giocatore.
 * I colori delle linee stanno in valutazioni/graficiConfronto.
 */

import {
  FAMIGLIE_ATTRIBUTO,
  type FamigliaAttributo,
  type FinestraCarico,
  type RuoloGiocatore,
} from '@calcio/shared';
import { dataBreve } from '../comuni/dati.js';

export const PRESET_GIOCATORI = [
  'tutti',
  'portiere',
  'difensore',
  'centrocampista',
  'attaccante',
] as const;
export type PresetGiocatori = (typeof PRESET_GIOCATORI)[number];

export const ETICHETTA_PRESET: Record<PresetGiocatori, string> = {
  tutti: 'Tutti',
  portiere: 'Portieri',
  difensore: 'Difensori',
  centrocampista: 'Centrocampisti',
  attaccante: 'Attaccanti',
};

export type ModoFinestra = FinestraCarico | 'personalizzato';

export const MODO_FINESTRA_DEFAULT: ModoFinestra = 'mese';

export function ordinaPerCognome<T extends { nome: string; cognome: string }>(
  rosa: readonly T[],
): T[] {
  return [...rosa].sort(
    (a, b) => a.cognome.localeCompare(b.cognome, 'it') || a.nome.localeCompare(b.nome, 'it'),
  );
}

export function idsAccesiIniziali(rosa: readonly { id: string }[]): string[] {
  const primo = rosa[0];
  return primo ? [primo.id] : [];
}

export type FiltroFamigliaAttributo = 'tutti' | FamigliaAttributo;

export function filtraAttributi<T extends { famiglia: string }>(
  attributi: readonly T[],
  famiglia: FiltroFamigliaAttributo,
): T[] {
  if (famiglia === 'tutti') return [...attributi];
  return attributi.filter((a) => a.famiglia === famiglia);
}

export function famigliePresenti(attributi: readonly { famiglia: string }[]): FamigliaAttributo[] {
  const presenti = new Set(attributi.map((a) => a.famiglia));
  return FAMIGLIE_ATTRIBUTO.filter((f) => presenti.has(f));
}

export function idsDelPreset(
  preset: PresetGiocatori,
  giocatori: readonly { id: string; ruolo: RuoloGiocatore }[],
): string[] {
  if (preset === 'tutti') return giocatori.map((g) => g.id);
  return giocatori.filter((g) => g.ruolo === preset).map((g) => g.id);
}

export type RigaGrafico = {
  chiave: string;
  doseSquadra: number;
  [k: string]: string | number;
};

export function righeGrafico(
  asse: readonly string[],
  rosa: readonly { serieDose: readonly number[] }[],
): RigaGrafico[] {
  return asse.map((chiave, i) => {
    const riga: RigaGrafico = {
      chiave,
      doseSquadra: rosa.reduce((s, g) => s + (g.serieDose[i] ?? 0), 0),
    };
    rosa.forEach((g, n) => {
      riga[`g${n}`] = g.serieDose[i] ?? 0;
    });
    return riga;
  });
}

/** Solo i periodi in cui la rosa ha un carico; senza dose in tutta la finestra, tutti. */
export function soloConCarico(righe: readonly RigaGrafico[]): RigaGrafico[] {
  const conCarico = righe.filter((r) => r.doseSquadra > 0);
  return conCarico.length > 0 ? conCarico : [...righe];
}

export type RigaDoseGiocatore = { chiave: string; dose: number };

export function righeDoseGiocatore(
  asse: readonly string[],
  serie: readonly number[],
): RigaDoseGiocatore[] {
  return asse.map((chiave, i) => ({ chiave, dose: serie[i] ?? 0 }));
}

/**
 * La dose del periodo sotto il filtro per categoria: con «Tutti» il totale,
 * altrimenti la somma degli attributi della famiglia, arrotondata all'unità.
 */
export function doseDelFiltro(
  totale: number,
  attributi: readonly { famiglia: string; ua: number }[],
  famiglia: FiltroFamigliaAttributo,
): number {
  if (famiglia === 'tutti') return totale;
  return Math.round(filtraAttributi(attributi, famiglia).reduce((t, a) => t + a.ua, 0));
}

/** Un intero con il punto delle migliaia, anche a quattro cifre (1.234, non 1234). */
export function migliaia(n: number): string {
  const segno = n < 0 ? '-' : '';
  return segno + String(Math.abs(Math.round(n))).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/** La dose nel tempo sotto il filtro per categoria, a un decimale per la famiglia. */
export function serieDelFiltro(
  serie: readonly number[],
  perFamiglia: Readonly<Record<FamigliaAttributo, readonly number[]>>,
  famiglia: FiltroFamigliaAttributo,
): number[] {
  if (famiglia === 'tutti') return [...serie];
  return perFamiglia[famiglia].map((x) => Math.round(x * 10) / 10);
}

export function attributiPerGrafico<T extends { nome: string; ua: number }>(
  attributi: readonly T[],
): T[] {
  return attributi.filter((a) => a.ua > 0).sort((a, b) => a.nome.localeCompare(b.nome, 'it'));
}

export function percorsoCarichiGiocatore(
  id: string,
  modo: ModoFinestra,
  da?: string,
  a?: string,
): string {
  if (modo === 'personalizzato') {
    if (!da || !a || da > a) return '';
    return `/giocatori/${id}/carichi?da=${da}&a=${a}`;
  }
  return `/giocatori/${id}/carichi?finestra=${modo}`;
}

export function ordinaVociTooltip<T extends { dataKey?: unknown; value?: unknown }>(
  voci: readonly T[],
): T[] {
  const n = (v: T) => (typeof v.value === 'number' ? v.value : 0);
  return [
    ...voci.filter((v) => v.dataKey === 'doseSquadra'),
    ...voci.filter((v) => v.dataKey !== 'doseSquadra').sort((a, b) => n(b) - n(a)),
  ];
}

export function percorsoCarichiSquadra(
  ss: string,
  modo: ModoFinestra,
  da?: string,
  a?: string,
): string {
  if (modo === 'personalizzato') {
    if (!da || !a || da > a) return '';
    return `/squadre-stagione/${ss}/carichi?da=${da}&a=${a}`;
  }
  return `/squadre-stagione/${ss}/carichi?finestra=${modo}`;
}

export function etichettaAsse(chiave: string, settimanale: boolean): string {
  if (!settimanale) return dataBreve(chiave);
  return `${giornoMese(chiave)}–${giornoMese(aggiungiGiorni(chiave, 6))}`;
}

export const ETICHETTA_MODO: Record<ModoFinestra, string> = {
  settimana: 'Settimana',
  mese: 'Mese',
  stagione: 'Stagione',
  personalizzato: 'Personalizzato',
};

function aggiungiGiorni(data: string, n: number): string {
  const [y, m, d] = data.split('-').map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + n));
  const due = (x: number) => String(x).padStart(2, '0');
  return `${t.getUTCFullYear()}-${due(t.getUTCMonth() + 1)}-${due(t.getUTCDate())}`;
}

function giornoMese(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}
